import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BookOpen, Printer } from "lucide-react";
import { API } from "@/lib/api";
import ProgramBuyModal from "@/components/ProgramBuyModal";

const fmtDate = (d) =>
  d
    ? new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : "";

function Sheet({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="program-sheet bg-white text-neutral-900 w-full max-w-[794px] mx-auto shadow-2xl border border-clay-border p-8 sm:p-12 min-h-[1120px]"
    >
      {children}
    </div>
  );
}

function RedBar({ children }) {
  return (
    <div className="border-y-4 border-double border-red-700 py-2.5 mb-10 mt-2">
      <h2 className="font-display font-black uppercase tracking-tight text-3xl text-center text-red-700">{children}</h2>
    </div>
  );
}

function NominationsTable({ rows }) {
  const half = Math.ceil(rows.length / 2);
  const columns = [rows.slice(0, half), rows.slice(half)];
  return (
    <div className="grid grid-cols-2 gap-4">
      {columns.map(
        (col, ci) =>
          col.length > 0 && (
            <table key={ci} className="w-full border-collapse text-[11px]">
              <thead>
                <tr>
                  {["Car #", "Driver"].map((h) => (
                    <th key={h} className="border border-neutral-400 bg-neutral-200 px-1.5 py-1 font-mono text-[8px] uppercase tracking-[0.2em] text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {col.map((n, i) => (
                  <tr key={`${n.car_number}-${n.driver_name}-${i}`} className="break-inside-avoid">
                    <td className="border border-neutral-300 px-1.5 py-1.5 font-bold w-10">{n.car_number}</td>
                    <td className="border border-neutral-300 px-1.5 py-1.5">{n.driver_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
      )}
    </div>
  );
}

export default function Program() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const key = new URLSearchParams(window.location.search).get("key") || "";

  useEffect(() => {
    fetch(`${API}/program/${eventId}${key ? `?key=${key}` : ""}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, [eventId, key]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-clay-bg text-clay-text font-mono text-sm uppercase tracking-[0.25em]">
        Program not found
      </div>
    );
  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center bg-clay-bg text-clay-muted font-mono text-sm uppercase tracking-[0.25em]">
        Loading program…
      </div>
    );

  if (data.locked)
    return (
      <div className="min-h-screen bg-clay-bg text-clay-text flex items-center justify-center px-4 py-16">
        <div className="max-w-lg w-full text-center border border-clay-border bg-clay-card p-8 sm:p-12">
          <img src="/logo-shield.jpg" alt="Mid-Western Speedway Darlington" className="w-48 mx-auto mb-6" />
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange mb-2">Digital Race Day Program</p>
          <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-2">{data.event.title}</h1>
          <p className="font-display font-bold uppercase text-xl text-clay-gold mb-6">{fmtDate(data.event.date)}</p>
          <p className="text-clay-muted text-sm leading-relaxed mb-8">
            Driver nominations and club sponsors — viewable and printable anytime from your own link.
          </p>
          <button
            data-testid="program-unlock-button"
            onClick={() => setBuyOpen(true)}
            className="w-full bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-colors duration-300 flex items-center justify-center gap-2"
          >
            <BookOpen className="w-5 h-5" /> Unlock Program — {data.price}
          </button>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mt-4">
            Already bought it? Use the link from your email receipt
          </p>
        </div>
        <ProgramBuyModal
          eventId={eventId}
          eventTitle={data.event.title}
          price={data.price}
          open={buyOpen}
          setOpen={setBuyOpen}
        />
      </div>
    );

  const { event, program, sponsors } = data;
  const nomGroups = [];
  (data.nominations || []).forEach((n) => {
    const cls = (n.racing_class || "").trim() || "Open Class";
    const g = nomGroups.find((g) => g[0] === cls);
    if (g) g[1].push(n);
    else nomGroups.push([cls, [n]]);
  });
  const price = program.price || "$2";
  const sanctionBody = (event.sanctioning_body || "").trim() || (/aasa/i.test(event.tag || "") ? "AASA" : "");
  const bodyLogo = { AASA: "/aasa-logo.png", "Speedway Australia": "/speedway-australia.png", JQMV: "/jqmv.png" }[sanctionBody];
  const coverSubtitle = program.cover_subtitle || "Official Race Day Program";
  const sponsorsNote = program.sponsors_note || "Please support the businesses that support your club.";
  const closingNote = program.closing_note || "Thank you for supporting Mid Western Speedway Darlington — Mortlake Car Club Inc.";

  return (
    <div className="program-root min-h-screen bg-clay-bg py-10 px-4 print:bg-white print:p-0">
      <div className="max-w-[794px] mx-auto mb-6 flex flex-wrap justify-between items-center gap-4 print:hidden">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted">
          Race Day Program — {fmtDate(event.date)}
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            data-testid="program-pdf-a5-button"
            href={`${API}/program/${eventId}/pdf?key=${key}&layout=a5`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider px-6 py-3 transition-colors"
          >
            <Printer className="w-4 h-4" /> Easy Print PDF — A5
          </a>
          <a
            data-testid="program-pdf-button"
            href={`${API}/program/${eventId}/pdf?key=${key}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 border border-clay-orange text-clay-orange hover:bg-clay-orange hover:text-clay-bg font-display font-bold uppercase tracking-wider px-6 py-3 transition-colors"
          >
            <Printer className="w-4 h-4" /> Folded Booklet PDF — A4 Landscape
          </a>
          <button
            data-testid="program-print-button"
            onClick={() => window.print()}
            className="flex items-center gap-2 border border-clay-border hover:border-clay-orange text-clay-muted hover:text-clay-text font-mono text-xs uppercase tracking-[0.2em] px-4 py-3 transition-colors"
          >
            Simple print
          </button>
        </div>
      </div>
      <div className="max-w-[794px] mx-auto mb-6 font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted print:hidden space-y-1.5">
        <p><span className="text-clay-gold">Easy Print PDF</span> — upright pages, prints correctly with your printer's normal settings. Nothing to change</p>
        <p><span className="text-clay-gold">Folded Booklet PDF</span> — in the print dialog set orientation to <span className="text-clay-gold">LANDSCAPE</span>, double-sided, <span className="text-clay-gold">"Flip on short edge"</span>, then fold in half — cover on the front, sponsors on the back</p>
      </div>

      <div className="space-y-8 print:space-y-0">
        {/* COVER */}
        <Sheet testid="program-cover">
          <div className="relative flex flex-col items-center justify-center text-center gap-8 min-h-[900px]">
            <div
              data-testid="program-price-star"
              className="absolute top-0 right-0 w-32 h-32 bg-red-700 rotate-12 flex flex-col items-center justify-center text-white shadow-xl"
              style={{ clipPath: "polygon(50% 0%, 61% 12%, 76% 6%, 79% 21%, 93% 18%, 89% 33%, 100% 38%, 92% 50%, 100% 62%, 89% 67%, 93% 82%, 79% 79%, 76% 94%, 61% 88%, 50% 100%, 39% 88%, 24% 94%, 21% 79%, 7% 82%, 11% 67%, 0% 62%, 8% 50%, 0% 38%, 11% 33%, 7% 18%, 21% 21%, 24% 6%, 39% 12%)" }}
            >
              <span className="font-display font-black text-3xl leading-none -rotate-12">{price}</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.15em] -rotate-12 mt-0.5">Each</span>
            </div>
            <img src="/logo-shield.jpg" alt="Mid-Western Speedway Darlington — Mortlake Car Club Inc, est. 1968" className="w-full max-w-[280px]" />
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.4em] text-neutral-500 mb-4">{coverSubtitle}</p>
              <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl leading-none text-neutral-900">
                {event.title}
              </h1>
              <p className="font-display font-bold uppercase text-2xl text-red-700 mt-5">{fmtDate(event.date)}</p>
            </div>
            {sanctionBody && (
              <div className="flex flex-col items-center" data-testid="cover-sanction-body">
                {bodyLogo ? (
                  <img
                    src={bodyLogo}
                    alt={sanctionBody}
                    className="h-20 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextSibling.style.display = "block";
                    }}
                  />
                ) : null}
                <span style={{ display: bodyLogo ? "none" : "block" }} className="font-display font-bold uppercase tracking-tight text-lg text-neutral-900">
                  {sanctionBody}
                </span>
              </div>
            )}
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">
              midwesternspeedwaydarlington.com
            </p>
          </div>
        </Sheet>

        {/* PRESIDENT'S LETTER */}
        <Sheet testid="program-letter">
          <img src="/logo-wide.jpg" alt="Mid-Western Speedway" className="h-9 object-contain mb-6 opacity-80" />
          <RedBar>From the President</RedBar>
          {program.president_letter ? (
            <div>
              <p className="leading-loose text-[15px] whitespace-pre-wrap">{program.president_letter}</p>
              {program.president_name && (
                <p className="mt-12 font-display font-bold uppercase tracking-wide text-lg">
                  {program.president_name}
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-normal mt-1">
                    President — Mortlake Car Club Inc.
                  </span>
                </p>
              )}
            </div>
          ) : (
            <p className="text-neutral-400 font-mono text-xs uppercase tracking-[0.25em] text-center py-24">
              President's letter — added by the club before printing
            </p>
          )}
        </Sheet>

        {/* THINGS TO DO & ON THE DAY — only printed when the club writes it */}
        {(program.activities || "").trim() && (
          <Sheet testid="program-activities">
            <img src="/logo-wide.jpg" alt="Mid-Western Speedway" className="h-9 object-contain mb-6 opacity-80" />
            <RedBar>Things To Do &amp; On The Day</RedBar>
            <p className="leading-loose text-[15px] whitespace-pre-wrap">{program.activities}</p>
          </Sheet>
        )}

        {/* NOMINATIONS — classes flow on shared pages to save space */}
        {nomGroups.length > 0 && (
          <Sheet testid="program-nominations">
            <img src="/logo-wide.jpg" alt="Mid-Western Speedway" className="h-9 object-contain mb-6 opacity-80" />
            <RedBar>Nominations</RedBar>
            <div className="space-y-6">
              {nomGroups.map(([cls, rows]) => (
                <div key={cls} data-testid={`program-nominations-${cls.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="break-inside-avoid">
                  <div className="bg-neutral-900 text-white px-3 py-2 flex items-center justify-between gap-3">
                    <span className="font-display font-black italic uppercase tracking-tight text-xl">{cls}</span>
                    <span data-testid={`nom-count-${cls.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 shrink-0">
                      {rows.length} {rows.length === 1 ? "entry" : "entries"}
                    </span>
                  </div>
                  <NominationsTable rows={rows} />
                </div>
              ))}
            </div>
          </Sheet>
        )}

        {/* SPONSORS BACK COVER */}
        <Sheet testid="program-sponsors">
          <RedBar>Our Club Sponsors</RedBar>
          <p className="text-center text-neutral-500 text-sm mb-10">{sponsorsNote}</p>
          <div className="grid grid-cols-3 gap-5 content-start">
            {sponsors.map((s) => (
              <div key={s.id || s.name} className="border border-neutral-300 p-4 flex items-center justify-center min-h-[110px]">
                {s.logo || s.photo_url ? (
                  <img src={s.logo || s.photo_url} alt={s.name} className="max-h-16 object-contain" />
                ) : (
                  <span className="font-display font-bold uppercase text-center tracking-tight text-sm">{s.name}</span>
                )}
              </div>
            ))}
          </div>
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400 mt-14">
            {closingNote}
          </p>
        </Sheet>
      </div>
    </div>
  );
}
